import {
  Invoice,
  ExtendedInvoice,
  GetAllInvoicesExtendedResponse,
  CreateInvoiceRequest,
  DeleteInvoiceFileRequest,
  AddInvoiceFileRequest,
} from "@/types/payment";
import { ListingService } from "@/services/listing/listingService";
import { PaymentService } from "../payment/paymentService";
import { UserManagementService } from "../user/userManagementService";
import { FileObject } from "@/types/shared/file";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";

export class InvoiceService {
  private static baseUrl = "/admin/invoices";

  static async getAllInvoices(): Promise<Invoice[]> {
    const response = await api.get<{ success: boolean; invoices: Invoice[] }>(
      this.baseUrl,
    );
    return response.invoices || [];
  }

  static async getInvoice(id: string): Promise<Invoice> {
    const response = await api.get<{ success: boolean; data: Invoice }>(
      `${this.baseUrl}/${id}`,
    );
    return response.data;
  }

  static async checkExistingInvoiceByListingId(
    listingId: string,
  ): Promise<Invoice | null> {
    // This is handled by backend - createInvoice will return 409 if invoice already exists
    // Just return null to indicate no check is needed
    return null;
  }

  static async createInvoice(
    request: CreateInvoiceRequest,
    replaceExisting: boolean = false,
  ): Promise<Invoice> {
    const { MediaService } = await import("../media/mediaService");

    // If replacing, delete existing invoice first
    if (replaceExisting) {
      try {
        // Get listing to find existing invoice
        const listingResponse = await api.get<{ success: boolean; data: any }>(
          `/listings/${request.listingId}`,
        );
        const listing = listingResponse.data;

        if (listing?.vehicleCardId) {
          // Try to find and delete existing invoice
          const invoicesResponse = await api.get<{
            success: boolean;
            invoices: any[];
          }>(`/admin/invoices?search=${listing.vehicleCardId}`);
          const existingInvoice = invoicesResponse.invoices?.find(
            (inv: any) => inv.vehicleCardId === listing.vehicleCardId,
          );

          if (existingInvoice) {
            await api.delete(`/admin/invoices/${existingInvoice.id}`);
          }
        }
      } catch (error) {}
    }

    // Create invoice WITHOUT file first (lazy upload)
    const createResponse = await api.post<{ success: boolean; data: any }>(
      this.baseUrl,
      {
        invoiceNumber: request.invoiceNumber,
        listingId: request.listingId,
        currency: "SEK",
        totalAmount: request.amount,
        status: "pending",
        dueAt: request.dueDate,
        replaceExisting,
      },
    );

    const createdInvoice = createResponse.data;

    // If file was provided, upload it now (lazy upload)
    if (request.invoiceFile && createdInvoice.id) {
      try {
        // Request presigned URL
        const { uploadUrl, s3Key } =
          await MediaService.requestPresignedUploadUrl(
            "invoice",
            createdInvoice.id,
            {
              originalName: request.invoiceFile.name,
              mimeType: request.invoiceFile.type,
              sizeBytes: request.invoiceFile.size,
            },
          );

        // Upload file to S3
        await MediaService.uploadFileToS3(uploadUrl, request.invoiceFile);

        // Create Media record with invoiceId
        const mediaResponse = await MediaService.createMediaAfterUpload(
          s3Key,
          {
            originalName: request.invoiceFile.name,
            mimeType: request.invoiceFile.type,
            sizeBytes: request.invoiceFile.size,
          },
          { invoiceId: createdInvoice.id },
        );

        // Update status to UPLOADED
        await MediaService.updateMediaStatus(mediaResponse.id, "UPLOADED");

        // Fetch updated invoice to get presigned download URL
        const updatedResponse = await api.get<{ success: boolean; data: any }>(
          `${this.baseUrl}/${createdInvoice.id}`,
        );
        const updatedInvoice = updatedResponse.data;

        // Map to Invoice format
        return {
          id: updatedInvoice.id,
          invoice_number: updatedInvoice.invoiceNumber,
          created_at: updatedInvoice.issuedAt,
          listingId: updatedInvoice.vehicleCardId, // Note: backend returns vehicleCardId, but we need listingId - this might need adjustment
          paymentId: "", // TODO: Get from payment application if exists
          invoice_file: updatedInvoice.documentMedia
            ? {
                id: updatedInvoice.documentMedia.id,
                url: "", // Will be fetched on-demand via presigned URL
                name: updatedInvoice.documentMedia.originalName,
                type: updatedInvoice.documentMedia.mimeType,
                size: updatedInvoice.documentMedia.sizeBytes,
                mediaId: updatedInvoice.documentMedia.id,
              }
            : (undefined as any),
        };
      } catch (error) {
        // Return invoice without file if upload fails
        return {
          id: createdInvoice.id,
          invoice_number: createdInvoice.invoiceNumber,
          created_at: createdInvoice.issuedAt,
          listingId: createdInvoice.vehicleCardId,
          paymentId: "",
          invoice_file: undefined as any,
        };
      }
    }

    // Map to Invoice format (no file)
    return {
      id: createdInvoice.id,
      invoice_number: createdInvoice.invoiceNumber,
      created_at: createdInvoice.issuedAt,
      listingId: createdInvoice.vehicleCardId,
      paymentId: "",
      invoice_file: undefined as any,
    };
  }

  static async updateInvoice(
    updateData: Partial<Invoice> & { id: string },
  ): Promise<Invoice> {
    const { id, ...data } = updateData;

    const response = await api.put<{ success: boolean; data: any }>(
      `/admin/invoices/${id}`,
      {
        invoiceNumber: data.invoice_number,
        currency: "SEK",
        status: data.invoice_file ? "pending" : undefined,
      },
    );

    const updatedInvoice = response.data;

    // Map to Invoice format
    return {
      id: updatedInvoice.id,
      invoice_number: updatedInvoice.invoiceNumber,
      created_at: updatedInvoice.issuedAt,
      listingId: updatedInvoice.vehicleCardId,
      paymentId: "",
      invoice_file: updatedInvoice.documentMedia
        ? {
            id: updatedInvoice.documentMedia.id,
            url: "",
            name: updatedInvoice.documentMedia.originalName,
            type: updatedInvoice.documentMedia.mimeType,
            size: updatedInvoice.documentMedia.sizeBytes,
            mediaId: updatedInvoice.documentMedia.id,
          }
        : (undefined as any),
    };
  }

  static async deleteInvoice(id: string): Promise<void> {
    await api.delete(`/admin/invoices/${id}`);
  }

  static async sendInvoice(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/${id}/send`,
    );
    return response;
  }

  static async getInvoicesByPaymentId(paymentId: string): Promise<Invoice[]> {
    // TODO: Backend endpoint for getting invoices by paymentId not yet implemented
    // For now, return empty array
    return [];
  }

  static async getAllInvoicesExtended(
    page: number = 1,
    limit: number = 10,
    status?: string,
    search?: string,
  ): Promise<GetAllInvoicesExtendedResponse> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (status && status !== "all") params.append("status", status);
    if (search) params.append("search", search);

    const response = await api.get<{
      success: boolean;
      invoices: ExtendedInvoice[];
      total: number;
      page: number;
      limit: number;
    }>(`/admin/invoices?${params.toString()}`);

    // Backend now returns ExtendedInvoice format directly, no mapping needed
    return {
      invoices: response.invoices,
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  // Delete invoice file (deletes Media object)
  static async deleteInvoiceFile(
    request: DeleteInvoiceFileRequest,
  ): Promise<{ status: string; message: string }> {
    try {
      // Use updateMedia endpoint to set mediaId to null
      const response = await api.put<{ success: boolean; data: unknown }>(
        `${this.baseUrl}/${request.invoiceId}/media`,
        { mediaId: null },
      );
      if (response.success) {
        return {
          status: "success",
          message: "Invoice file deleted successfully",
        };
      }
      throw new Error("Failed to delete invoice file");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete invoice file";
      throw new Error(errorMessage);
    }
  }

  // Add invoice file (lazy upload pattern)
  static async addInvoiceFile(
    request: AddInvoiceFileRequest,
  ): Promise<FileObject> {
    try {
      const { MediaService } = await import("../media/mediaService");

      // Request presigned URL
      const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
        "invoice",
        request.invoiceId,
        {
          originalName: request.invoiceFile.name,
          mimeType: request.invoiceFile.type,
          sizeBytes: request.invoiceFile.size,
        },
      );

      // Upload file to S3
      await MediaService.uploadFileToS3(uploadUrl, request.invoiceFile);

      // Create Media record with invoiceId
      const mediaResponse = await MediaService.createMediaAfterUpload(
        s3Key,
        {
          originalName: request.invoiceFile.name,
          mimeType: request.invoiceFile.type,
          sizeBytes: request.invoiceFile.size,
        },
        { invoiceId: request.invoiceId },
      );

      // Update status to UPLOADED
      await MediaService.updateMediaStatus(mediaResponse.id, "UPLOADED");

      return {
        id: mediaResponse.id,
        url: "", // Will be fetched on-demand via presigned URL
        name: mediaResponse.originalName,
        type: mediaResponse.mimeType,
        size: mediaResponse.sizeBytes,
        mediaId: mediaResponse.id,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to add invoice file";
      throw new Error(errorMessage);
    }
  }
}
